/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-12-09
 */

// Add third-party dependencies.
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
  useTheme,
  CircularProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Star as StarIcon,
} from '@mui/icons-material';

/**
 * Pricing plan data structure.
 */
interface PricingPlan {
  id: string;
  name: string;
  monthlyPrice: number;
  yearlyPrice: number;
  badge?: string;
  features: string[];
  description: string;
  ctaText: string;
  ctaVariant?: 'contained' | 'outlined';
  popular?: boolean;
  premium?: boolean;
}

/**
 * Props for the PricingCard component.
 */
interface PricingCardProps {
  /**
   * The pricing plan data.
   */
  plan: PricingPlan;
  /**
   * Whether annual billing is selected.
   */
  isAnnual: boolean;
  /**
   * Whether a plan selection is currently being processed.
   */
  isProcessing?: boolean;
  /**
   * Callback when the plan is selected.
   */
  onSelect: () => void;
}

/**
 * Pricing card component that displays a single pricing plan.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The pricing card component
 *
 * @example
 * return (
 *   <PricingCard
 *     plan={plan}
 *     isAnnual={isAnnual}
 *     onSelect={() => handlePlanSelect(plan.id)}
 *   />
 * );
 */
const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  isAnnual,
  isProcessing = false,
  onSelect,
}) => {
  const theme = useTheme();

  // Calculate price and savings
  const displayPrice = isAnnual ? plan.yearlyPrice : plan.monthlyPrice;
  const priceLabel = isAnnual ? '/year' : '/month';
  const monthlyEquivalent =
    isAnnual && plan.yearlyPrice > 0
      ? Math.round((plan.yearlyPrice / 12) * 100) / 100
      : null;

  // Determine card styling based on plan type
  const isHighlighted = plan.popular || plan.premium;
  const elevation = isHighlighted ? 8 : 2;

  // Determine border color based on plan type
  let borderColor = 'transparent';
  if (plan.popular) {
    borderColor = theme.palette.primary.main;
  } else if (plan.premium) {
    borderColor = theme.palette.secondary.main;
  }

  return (
    <Card
      elevation={elevation}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        borderRadius: 3,
        border:
          borderColor !== 'transparent'
            ? `2px solid ${borderColor}`
            : undefined,
        transition: 'all 0.3s ease',
        '&:hover': {
          elevation: isHighlighted ? 12 : 6,
          transform: 'translateY(-4px)',
        },
      }}
    >
      {/* Badge */}
      {plan.badge && (
        <Box
          sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1,
          }}
        >
          <Chip
            label={plan.badge}
            color={plan.popular ? 'primary' : 'secondary'}
            variant="filled"
            icon={plan.popular ? <StarIcon /> : undefined}
            sx={{
              fontWeight: 600,
              fontSize: '0.75rem',
              px: 1,
            }}
          />
        </Box>
      )}

      <CardContent
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          pt: plan.badge ? 5 : 3,
          pb: 2,
        }}
      >
        {/* Plan Name */}
        <Typography
          variant="h5"
          component="h3"
          sx={{
            fontWeight: 700,
            textAlign: 'center',
            mb: 1,
            color: isHighlighted ? 'primary.main' : 'text.primary',
          }}
        >
          {plan.name}
        </Typography>

        {/* Description */}
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            textAlign: 'center',
            mb: 3,
            minHeight: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {plan.description}
        </Typography>

        {/* Price */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography
            variant="h3"
            component="div"
            sx={{
              fontWeight: 700,
              color: isHighlighted ? 'primary.main' : 'text.primary',
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'center',
              gap: 0.5,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontSize: '0.6em',
                fontWeight: 500,
              }}
            >
              $
            </Typography>
            {displayPrice}
            <Typography
              component="span"
              sx={{
                fontSize: '0.4em',
                fontWeight: 400,
                color: 'text.secondary',
              }}
            >
              {plan.monthlyPrice === 0 ? ' / forever' : priceLabel}
            </Typography>
          </Typography>

          {/* Annual savings indicator */}
          {isAnnual && plan.yearlyPrice > 0 && monthlyEquivalent && (
            <Typography
              variant="body2"
              color="success.main"
              sx={{
                mt: 1,
                fontWeight: 600,
              }}
            >
              Save ${(plan.monthlyPrice * 12 - plan.yearlyPrice).toFixed(0)} (2
              months free!)
            </Typography>
          )}

          {/* Monthly equivalent for annual plans */}
          {isAnnual && monthlyEquivalent && plan.yearlyPrice > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              ${monthlyEquivalent.toFixed(2)}/month billed annually
            </Typography>
          )}
        </Box>

        {/* Features List */}
        <List
          sx={{
            flex: 1,
            py: 0,
          }}
        >
          {plan.features.map(feature => (
            <ListItem
              key={feature}
              sx={{
                px: 0,
                py: 0.5,
                alignItems: 'flex-start',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  mt: 0.5,
                }}
              >
                <CheckIcon
                  sx={{
                    fontSize: 20,
                    color: isHighlighted ? 'primary.main' : 'success.main',
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={feature}
                primaryTypographyProps={{
                  variant: 'body2',
                  sx: { fontWeight: 500 },
                }}
              />
            </ListItem>
          ))}
        </List>
      </CardContent>

      {/* CTA Button */}
      <CardActions sx={{ p: 3, pt: 0 }}>
        <Button
          variant={plan.ctaVariant || 'contained'}
          fullWidth
          size="large"
          disabled={isProcessing}
          onClick={onSelect}
          startIcon={isProcessing ? <CircularProgress size={20} /> : undefined}
          sx={{
            py: 1.5,
            fontSize: '1rem',
            fontWeight: 600,
            borderRadius: 2,
            ...(plan.ctaVariant === 'outlined' &&
              isHighlighted && {
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                },
              }),
          }}
        >
          {isProcessing ? 'Processing...' : plan.ctaText}
        </Button>
      </CardActions>
    </Card>
  );
};

export default PricingCard;
